import type { JobFailureStatus, JobFailureView } from '@ai-sales/shared'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { fetchJobFailures, updateJobFailure } from '../api/workspace.api.js'
import { Badge, type Tone } from '../components/badge.js'
import { Button } from '../components/button.js'
import { DataTable, EmptyState, type Column } from '../components/data-table.js'
import { ErrorPanel } from '../components/error-panel.js'
import { PageHeader, Panel } from '../components/page.js'
import { formatDateTime, formatRelative, plural } from '../lib/format.js'
import { useCurrentWorkspace } from '../workspace/workspace-context.js'

const STATUS_LABELS: Record<JobFailureStatus, string> = {
  open: 'не разобран',
  acknowledged: 'в работе',
  resolved: 'решён',
}

const STATUS_TONES: Record<JobFailureStatus, Tone> = {
  open: 'bad',
  acknowledged: 'warn',
  resolved: 'ok',
}

const FILTERS: { value: '' | JobFailureStatus; label: string }[] = [
  { value: 'open', label: 'Не разобранные' },
  { value: 'acknowledged', label: 'В работе' },
  { value: 'resolved', label: 'Решённые' },
  { value: '', label: 'Все' },
]

/**
 * Журнал сбоев — «мёртвое письмо» на языке интерфейса.
 *
 * Сюда попадают только задачи, исчерпавшие все попытки: промежуточные
 * падения лечит повтор, и показывать их значило бы утопить настоящую
 * проблему в шуме. Одинаковые сбои слиты в одну строку со счётчиком —
 * если сломался внешний сервис и упало три тысячи задач, оператору нужна
 * одна строка с числом, а не три тысячи строк.
 *
 * По умолчанию открыт фильтр «не разобранные»: экран нужен для работы
 * с текущими проблемами, а не для просмотра истории.
 */
export function JobFailuresPage() {
  const workspace = useCurrentWorkspace()
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<'' | JobFailureStatus>('open')

  const failures = useInfiniteQuery({
    queryKey: ['job-failures', workspace.id, status],
    queryFn: ({ pageParam }) =>
      fetchJobFailures(workspace.id, {
        limit: 50,
        ...(pageParam ? { cursor: pageParam } : {}),
        ...(status ? { status } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    /**
     * Экран обновляется сам, пока открыта только первая страница.
     *
     * Это не украшение. Сбои появляются не тогда, когда человек нажимает
     * «обновить», а через полминуты после того, как задача исчерпала
     * попытки, — то есть уже после того, как он открыл экран. Без
     * самообновления дежурный смотрит на «сбоев нет», пока они копятся.
     *
     * Как только человек начинает листать историю, обновление
     * прекращается: подгружать все страницы каждые двадцать секунд
     * дорого, а главное — список не должен двигаться под руками, когда
     * в нём что-то ищут.
     */
    refetchInterval: (query) => ((query.state.data?.pages.length ?? 1) > 1 ? false : 20_000),
  })

  const changeStatus = useMutation({
    mutationFn: (input: { id: string; status: JobFailureStatus }) =>
      updateJobFailure(workspace.id, input.id, input.status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['job-failures', workspace.id] })
      void queryClient.invalidateQueries({ queryKey: ['queue-stats', workspace.id] })
    },
  })

  const rows = failures.data?.pages.flatMap((page) => page.items) ?? []

  const columns: Column<JobFailureView>[] = [
    {
      key: 'job',
      header: 'Задача',
      render: (failure) => (
        <div className="min-w-0">
          <p className="font-mono text-sm text-ink">{failure.jobName}</p>
          <p className="text-xs whitespace-nowrap text-muted">
            попыток {failure.attemptsMade} из {failure.attemptsAllowed}
          </p>
          {failure.occurrences > 1 && (
            <Badge tone="bad" className="mt-1">
              {failure.occurrences} {plural(failure.occurrences, 'раз', 'раза', 'раз')}
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'reason',
      header: 'Причина',
      render: (failure) => (
        <details className="min-w-0 max-w-xl">
          <summary className="cursor-pointer text-sm text-ink-soft">
            {truncate(failure.failedReason, 120)}
          </summary>
          {failure.stackExcerpt && (
            <pre className="mt-2 max-h-48 overflow-auto rounded border border-line-soft bg-raised p-2 font-mono text-xs whitespace-pre-wrap text-muted">
              {failure.stackExcerpt}
            </pre>
          )}
          {failure.requestId && (
            <p className="mt-2 text-xs text-muted">
              Идентификатор запроса:{' '}
              <code className="font-mono select-all">{failure.requestId}</code>
            </p>
          )}
        </details>
      ),
    },
    {
      key: 'when',
      header: 'Когда',
      render: (failure) => (
        <div className="whitespace-nowrap">
          <p className="text-sm text-ink" title={formatDateTime(failure.lastFailedAt)}>
            {formatRelative(failure.lastFailedAt)}
          </p>
          {failure.occurrences > 1 && (
            <p className="text-xs text-muted" title={formatDateTime(failure.firstFailedAt)}>
              впервые {formatRelative(failure.firstFailedAt)}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Состояние',
      render: (failure) => (
        <Badge tone={STATUS_TONES[failure.status]}>{STATUS_LABELS[failure.status]}</Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (failure) => (
        <div className="flex justify-end gap-1 whitespace-nowrap">
          {failure.status === 'open' && (
            <Button
              size="sm"
              variant="ghost"
              disabled={changeStatus.isPending}
              onClick={() => changeStatus.mutate({ id: failure.id, status: 'acknowledged' })}
            >
              Взять в работу
            </Button>
          )}
          {failure.status !== 'resolved' && (
            <Button
              size="sm"
              disabled={changeStatus.isPending}
              onClick={() => changeStatus.mutate({ id: failure.id, status: 'resolved' })}
            >
              {/*
                «Пометить решённым», а не просто «Решён»: рядом стоит фильтр
                «Решённые», и две подписи, различающиеся окончанием, путают —
                и человека, и всё, что обращается к интерфейсу по имени кнопки
              */}
              Пометить решённым
            </Button>
          )}
          {failure.status === 'resolved' && (
            <Button
              size="sm"
              variant="ghost"
              disabled={changeStatus.isPending}
              onClick={() => changeStatus.mutate({ id: failure.id, status: 'open' })}
            >
              Открыть заново
            </Button>
          )}
        </div>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Сбои задач"
        description="Задачи, исчерпавшие все попытки. Пометка «решён» снимает склейку: следующий такой же сбой создаст новую запись, и возврат проблемы будет виден."
        actions={
          <Button size="sm" loading={failures.isFetching} onClick={() => void failures.refetch()}>
            Обновить
          </Button>
        }
      />

      <div className="flex flex-col gap-4">
        <Panel>
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map((filter) => (
              <Button
                key={filter.value || 'all'}
                size="sm"
                variant={status === filter.value ? 'primary' : 'ghost'}
                onClick={() => setStatus(filter.value)}
              >
                {filter.label}
              </Button>
            ))}
          </div>
        </Panel>

        {changeStatus.error && <ErrorPanel error={changeStatus.error} />}

        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(failure) => failure.id}
          isLoading={failures.isPending}
          isRefreshing={
            failures.isFetching && !failures.isPending && !failures.isFetchingNextPage
          }
          empty={
            <EmptyState
              title={status === 'open' ? 'Не разобранных сбоев нет' : 'Записей нет'}
              description={
                status === 'open'
                  ? 'Это хорошая новость: все фоновые задачи либо выполнились, либо их сбои уже разобрали.'
                  : 'По выбранному фильтру ничего не нашлось.'
              }
            />
          }
        />

        {failures.error && (
          <ErrorPanel error={failures.error} onRetry={() => void failures.refetch()} />
        )}

        {failures.hasNextPage && (
          <div>
            <Button
              loading={failures.isFetchingNextPage}
              onClick={() => void failures.fetchNextPage()}
            >
              Показать ещё
            </Button>
          </div>
        )}
      </div>
    </>
  )
}

function truncate(value: string, limit: number): string {
  return value.length <= limit ? value : `${value.slice(0, limit)}…`
}
